import React from "react";
import { isMobile } from "react-device-detect";
import styled from "styled-components";
import { userTaskDataCollection } from "@lifeat/shared/task-management/TaskManagementTypes";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { useSignal, useSignalEffect } from "@preact/signals-react";
import { FirestoreService } from "@service/FirestoreService";
import { RecurringTaskDetails } from "../../../../../features/task-management/components/RecurringTaskDetails";
import { SingularTaskDetails } from "../../../../../features/task-management/components/SingularTaskDetails";
import { buildClientRecurringTask } from "../../../../../features/task-management/useRecurringTasks";
import { TaskListContext } from "../../../../task-management/TaskListContext";
import { isTodoListDataMigrationProcessing$, selectedDetail$ } from "../../../../task-management/TaskManagementGlobals";
import {
    ClientRecurringTask,
    ClientTaskItemType,
    TaskServiceInterface,
} from "../../../../task-management/TaskManagementTypes";
import { TaskFilterDropdown } from "../../../../task-management/components/TaskFilterDropdown";
import TaskList from "../../../../task-management/components/TaskList";
import { TaskProjectFilterDropdown } from "../../../../task-management/components/TaskProjectFilterDropdown";
import { TaskTagFilterDropdown } from "../../../../task-management/components/TaskTagFilterDropdown";
import Widget, { WidgetProps } from "../Widget";
import TaskListMigrationIndicator from "./TaskListMigrationIndicator";

const StyledWidget = styled(Widget)`
    .widget-header-content {
        margin: 8px;
    }
`;

const HeaderRow = styled.div`
    display: flex;
    gap: 10px;
`;

type TaskDetailsState =
    | { type: "none" }
    | { type: "loaded-singular"; taskId: string; task: ClientTaskItemType }
    | { type: "loaded-recurring"; task: ClientRecurringTask };

interface TaskListWidgetProps extends WidgetProps {
    taskData: TaskServiceInterface;
}
export default function TaskListWidget(props: TaskListWidgetProps) {
    const { lightMode }: AppearanceContextType = useAppearanceContext();
    const { user }: UserContextType = useUserContext();
    const fs = FirestoreService();

    const taskDetailsState$ = useSignal<TaskDetailsState>({ type: "none" });

    useSignalEffect(() => {
        if (selectedDetail$.value == null) {
            taskDetailsState$.value = { type: "none" };
        } else if (selectedDetail$.value.type === "singular") {
            const selectedTaskId = selectedDetail$.value.taskId;
            const taskFromState = props.taskData.taskItems$.value[selectedTaskId];
            if (taskFromState == null) {
                // Load the task from database.
                const node = userTaskDataCollection.at(user?.uid).at("tasks").at(selectedTaskId).node;
                const unsubscribe = fs.getDocumentListener(node, (payload) => {
                    if (payload.success && payload.value != null) {
                        taskDetailsState$.value = {
                            type: "loaded-singular",
                            taskId: selectedTaskId,
                            task: { ...payload.value, type: "singular", isDraft: false },
                        };
                    }
                });
                return () => {
                    unsubscribe();
                };
            } else {
                // Load the task from client state.
                taskDetailsState$.value = {
                    type: "loaded-singular",
                    taskId: selectedDetail$.value.taskId,
                    task: taskFromState,
                };
            }
            if (!props.isOpen) {
                props.openWidget();
            }
        } else if (selectedDetail$.value.type === "recurring") {
            const selectedMasterId = selectedDetail$.value.masterId;
            const selectedIndex = selectedDetail$.value.index;
            const masterFromState = props.taskData.recurringMasters$.value[selectedMasterId];
            const overrideFromState = props.taskData.recurringOverrides$.value[selectedMasterId]?.[selectedIndex];
            if (masterFromState == null) {
                // The task in not in the state yet.
                // Do nothing.
                return undefined;
            }
            taskDetailsState$.value = {
                type: "loaded-recurring",
                task: buildClientRecurringTask({
                    master: masterFromState,
                    masterId: selectedMasterId,
                    index: selectedIndex,
                    override: overrideFromState,
                }),
            };
        }
        return undefined;
    });

    const singularTaskDetailView =
        taskDetailsState$.value.type === "loaded-singular" ? (
            <SingularTaskDetails
                task={{ id: taskDetailsState$.value.taskId, document: taskDetailsState$.value.task }}
                amplitudeEventSource={props.amplitudeEventSource}
            />
        ) : null;

    const recurringTaskDetailView =
        taskDetailsState$.value.type === "loaded-recurring" ? (
            <RecurringTaskDetails
                task={taskDetailsState$.value.task}
                amplitudeEventSource={props.amplitudeEventSource}
            />
        ) : null;

    const taskList = (
        <TaskList
            taskList$={props.taskData.taskItems$}
            amplitudeEventSource={props.amplitudeEventSource}
            showProgressBar={true}
        />
    );

    const filterHeader =
        taskDetailsState$.value.type !== "none" ? (
            <></>
        ) : (
            <HeaderRow>
                <TaskTagFilterDropdown amplitudeEventSource={props.amplitudeEventSource} />
                <TaskProjectFilterDropdown amplitudeEventSource={props.amplitudeEventSource} />
                <TaskFilterDropdown />
            </HeaderRow>
        );

    const mobileHeight = (window.innerHeight - 110) * 0.8; // height - top nav and bottom nav height * 80%
    return (
        <TaskListContext.Provider
            value={{
                ...props.taskData,
            }}
        >
            <StyledWidget
                overrideWidth={400}
                overrideHeight={isMobile ? mobileHeight : 400}
                {...props}
                lightMode={lightMode}
                headerContent={filterHeader}
                footerAutoHide={false}
            >
                {isTodoListDataMigrationProcessing$.value && <TaskListMigrationIndicator />}
                {!isTodoListDataMigrationProcessing$.value &&
                    (singularTaskDetailView ?? recurringTaskDetailView ?? taskList)}
            </StyledWidget>
        </TaskListContext.Provider>
    );
}
